import { useState, useCallback } from "react";
import { useAuthenticatedFetch } from "./useAuthenticatedFetch";

export interface Vendor {
  id: string;
  name: string;
  code: string | null;
  accountNumber: string | null;
  contactName: string | null;
  email: string | null;
  orderEmail: string | null;
  fax: string | null;
  phone: string | null;
  website: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  country: string | null;
  mailingAddressLine1: string | null;
  mailingAddressLine2: string | null;
  mailingCity: string | null;
  mailingProvince: string | null;
  mailingPostalCode: string | null;
  mailingCountry: string | null;
  discountPercent: number;
  paymentTerms: string | null;
  leadTimeDays: number;
  notes: string | null;
  isActive: boolean;
  productCount?: number;
  categoryMinLevels?: CategoryMinLevel[];
}

export interface CategoryMinLevel {
  id?: string;
  category: string;
  minWeeksSupply: number;
}

export function useVendors() {
  const authFetch = useAuthenticatedFetch();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadVendors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch("/api/vendors");
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to load vendors");
      setVendors(json.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const loadVendor = useCallback(
    async (id: string): Promise<Vendor | null> => {
      try {
        const res = await authFetch(`/api/vendors/${id}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return json.data;
      } catch (err: any) {
        setError(err.message);
        return null;
      }
    },
    [authFetch]
  );

  const createVendor = useCallback(
    async (data: Partial<Vendor> & { categoryMinLevels?: CategoryMinLevel[] }): Promise<Vendor | null> => {
      try {
        const res = await authFetch("/api/vendors", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return json.data;
      } catch (err: any) {
        setError(err.message);
        return null;
      }
    },
    [authFetch]
  );

  const updateVendor = useCallback(
    async (id: string, data: Partial<Vendor>): Promise<Vendor | null> => {
      try {
        const res = await authFetch(`/api/vendors/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return json.data;
      } catch (err: any) {
        setError(err.message);
        return null;
      }
    },
    [authFetch]
  );

  const deleteVendor = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const res = await authFetch(`/api/vendors/${id}`, {
          method: "DELETE",
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return true;
      } catch (err: any) {
        setError(err.message);
        return false;
      }
    },
    [authFetch]
  );

  const updateCategoryMinLevels = useCallback(
    async (vendorId: string, levels: CategoryMinLevel[]): Promise<boolean> => {
      try {
        const res = await authFetch(
          `/api/vendors/${vendorId}/category-min-levels`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(levels),
          }
        );
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return true;
      } catch (err: any) {
        setError(err.message);
        return false;
      }
    },
    [authFetch]
  );

  return {
    vendors,
    loading,
    error,
    loadVendors,
    loadVendor,
    createVendor,
    updateVendor,
    deleteVendor,
    updateCategoryMinLevels,
  };
}
